import React, { memo, useEffect, useRef, useState } from 'react';
import { TrendingUp, TrendingDown, Zap, Target, Activity, Volume2, VolumeX, BarChart3, Trophy, XCircle } from 'lucide-react';

// Signal State Constants
const STATE_NEUTRAL = "NEUTRAL";
const STATE_BUILDING = "BUILDING";
const STATE_EXECUTE = "EXECUTE";

// ============================================
// SOUND ALERT SYSTEM - Enhanced with BUILDING alert
// ============================================
const useSignalSound = (enabled = true) => {
  const audioContextRef = useRef(null);
  const lastAlertTimeRef = useRef(0);
  const lastStateRef = useRef(STATE_NEUTRAL);
  
  const playAlertSound = (type = 'buy', intensity = 'execute') => {
    if (!enabled) return;
    
    // Prevent sound spam (min 2 seconds between alerts for EXECUTE, 5s for BUILDING)
    const now = Date.now();
    const cooldown = intensity === 'building' ? 5000 : 2000;
    if (now - lastAlertTimeRef.current < cooldown) return;
    lastAlertTimeRef.current = now;
    
    try {
      // Create audio context on demand
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      if (intensity === 'building') {
        // BUILDING alert - softer, shorter notification tone
        oscillator.frequency.setValueAtTime(523, ctx.currentTime); // C5
        oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1); // E5
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime); // Softer volume
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.15);
      } else {
        // EXECUTE alert - prominent, attention-grabbing
        if (type === 'buy') {
          // Rising tone for BUY
          oscillator.frequency.setValueAtTime(440, ctx.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15);
          oscillator.type = 'sine';
        } else {
          // Falling tone for SELL
          oscillator.frequency.setValueAtTime(660, ctx.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(330, ctx.currentTime + 0.15);
          oscillator.type = 'triangle';
        }
        
        // Volume envelope - louder for EXECUTE
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.2);
      }
    } catch (e) {
      console.warn('Sound alert failed:', e);
    }
  };
  
  return { playAlertSound, lastStateRef };
};

// ============================================
// CIRCULAR PROBABILITY GAUGE
// ============================================
const ProbabilityGauge = memo(({ probability, signalState }) => {
  const radius = 40;
  const stroke = 6;
  const normalizedRadius = radius - stroke * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (probability / 100) * circumference;
  
  // Color based on signal state
  const getColor = () => {
    if (signalState === STATE_EXECUTE) return '#22c55e'; // Green
    if (signalState === STATE_BUILDING) return '#d29922'; // Yellow
    return '#484f58'; // Gray
  };
  
  const color = getColor();
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          stroke="#21262d"
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        {/* Progress circle */}
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease' }}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
      {/* Probability text in center */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold font-mono" style={{ color }}>
          {probability}
        </span>
        <span className="text-[8px] text-muted-foreground uppercase">WIN %</span>
      </div>
    </div>
  );
});

ProbabilityGauge.displayName = 'ProbabilityGauge';

// ============================================
// HFT SIGNAL PANEL ("Sniper Scope") - THREE-STATE LOGIC
// ============================================
export const HFTSignalPanel = memo(({ signal, latestCandle }) => {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const { playAlertSound, lastStateRef } = useSignalSound(soundEnabled);
  const lastSignalRef = useRef(null);
  
  // Track signal changes and play appropriate sound
  useEffect(() => {
    const data = signal || latestCandle;
    if (!data) return;
    
    const signalType = data.signal;
    const signalState = data.signal_state || STATE_NEUTRAL;
    const probability = data.probability || 0;
    const direction = data.direction || "";
    
    // Only alert on state transitions (not repeated same-state signals)
    if (signalState !== lastStateRef.current) {
      lastStateRef.current = signalState;
      
      // EXECUTE signal (>= 80%) - prominent alert
      if (signalState === STATE_EXECUTE && signalType !== 'NEUTRAL') {
        const signalKey = `${signalType}-${probability}-${data.time || Date.now()}`;
        if (lastSignalRef.current !== signalKey) {
          lastSignalRef.current = signalKey;
          playAlertSound(signalType === 'BUY' ? 'buy' : 'sell', 'execute');
        }
      }
      // BUILDING signal (60-79%) - softer notification
      else if (signalState === STATE_BUILDING && direction) {
        playAlertSound(direction === 'BULLISH' ? 'buy' : 'sell', 'building');
      }
    }
  }, [signal, latestCandle, playAlertSound, lastStateRef]);
  
  if (!signal && !latestCandle) {
    return (
      <div className="p-3 bg-surface/50">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-accent" />
          <span className="text-xs font-semibold text-muted-foreground uppercase">Sniper Scope</span>
        </div>
        <div className="text-center text-muted-foreground text-xs py-4">
          Waiting for data...
        </div>
      </div>
    );
  }

  const data = signal || latestCandle || {};
  const signalType = data.signal || "NEUTRAL";
  const signalState = data.signal_state || STATE_NEUTRAL;
  const probability = data.probability || 0;
  const factors = data.factors || data.signal_factors || [];
  const hasSweep = data.has_sweep || false;
  const sweepDirection = data.sweep_direction || "";
  const imbalanceType = data.imbalance_type || "";
  const trapDetected = data.trap_detected || "";
  const direction = data.direction || "";

  // THREE-STATE Signal styling
  const getSignalStyle = () => {
    if (signalState === STATE_EXECUTE) {
      if (signalType === "BUY") {
        return {
          bg: "bg-buy/20",
          border: "border-buy",
          text: "text-buy",
          icon: TrendingUp,
          glow: "shadow-[0_0_20px_rgba(34,197,94,0.5)] animate-pulse",
          label: "EXECUTE BUY",
          labelColor: "bg-buy text-background"
        };
      } else {
        return {
          bg: "bg-sell/20",
          border: "border-sell",
          text: "text-sell",
          icon: TrendingDown,
          glow: "shadow-[0_0_20px_rgba(239,68,68,0.5)] animate-pulse",
          label: "EXECUTE SELL",
          labelColor: "bg-sell text-background"
        };
      }
    } else if (signalState === STATE_BUILDING) {
      return {
        bg: "bg-warning/10",
        border: "border-warning",
        text: "text-warning",
        icon: Activity,
        glow: "shadow-[0_0_10px_rgba(210,153,34,0.3)]",
        label: "BUILDING...",
        labelColor: "bg-warning text-background"
      };
    } else {
      return {
        bg: "bg-surface",
        border: "border-border",
        text: "text-muted-foreground",
        icon: Activity,
        glow: "",
        label: "NEUTRAL",
        labelColor: "bg-muted text-muted-foreground"
      };
    }
  };

  const style = getSignalStyle();
  const SignalIcon = style.icon;

  return (
    <div className="p-3" data-testid="hft-signal-panel">
      {/* Header with Sound Toggle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-accent" />
          <span className="text-xs font-semibold text-muted-foreground uppercase">Sniper Scope</span>
        </div>
        <div className="flex items-center gap-2">
          {(hasSweep || trapDetected) && (
            <Zap className={`w-4 h-4 ${trapDetected ? 'text-sell animate-pulse' : 'text-warning'}`} />
          )}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-1 rounded transition-colors ${soundEnabled ? 'text-accent' : 'text-muted-foreground'}`}
            title={soundEnabled ? 'Sound alerts ON (click to mute)' : 'Sound alerts OFF (click to enable)'}
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Main Signal Card - Three-State Design */}
      <div className={`${style.bg} ${style.border} border-2 rounded p-3 ${style.glow} transition-all`}>
        {/* Signal State Badge */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className={`px-3 py-1 rounded text-xs font-bold uppercase ${style.labelColor}`}>
            {style.label}
          </div>
          {direction && signalState !== STATE_NEUTRAL && (
            <div className={`text-xs font-mono ${direction === 'BULLISH' ? 'text-buy' : 'text-sell'}`}>
              {direction}
            </div>
          )}
        </div>
        
        {/* Signal + Probability Row */}
        <div className="flex items-center justify-between gap-3">
          {/* Signal Type - Large */}
          <div className="flex items-center gap-2">
            <SignalIcon className={`w-8 h-8 ${style.text}`} />
            <div>
              <div className={`font-bold text-xl ${style.text} leading-none`}>
                {signalType}
              </div>
              <div className="text-[9px] text-muted-foreground uppercase mt-0.5">
                Signal
              </div>
            </div>
          </div>
          
          {/* Probability Gauge */}
          <ProbabilityGauge probability={probability} signalState={signalState} />
        </div>

        {/* HFT Event Flags */}
        {(hasSweep || imbalanceType || trapDetected) && (
          <div className="flex flex-wrap gap-1 mt-3 pt-2 border-t border-border/30">
            {hasSweep && (
              <span className={`px-2 py-0.5 text-[10px] rounded font-semibold ${
                sweepDirection === "UP" ? "bg-buy/30 text-buy" : "bg-sell/30 text-sell"
              }`}>
                ⚡ {sweepDirection} SWEEP
              </span>
            )}
            {imbalanceType && (
              <span className={`px-2 py-0.5 text-[10px] rounded font-semibold ${
                imbalanceType === "BULLISH" ? "bg-buy/30 text-buy" : "bg-sell/30 text-sell"
              }`}>
                🔥 {imbalanceType}
              </span>
            )}
            {trapDetected && (
              <span className="px-2 py-0.5 text-[10px] rounded font-semibold bg-sell/50 text-sell animate-pulse">
                🪤 {trapDetected}
              </span>
            )}
          </div>
        )}

        {/* Signal Factors ("The Why") */}
        {factors.length > 0 && (
          <div className="border-t border-border/30 pt-2 mt-3">
            <div className="text-[9px] text-muted-foreground uppercase mb-1 font-semibold">Why this signal:</div>
            <div className="space-y-0.5">
              {factors.slice(0, 4).map((factor, i) => (
                <div key={i} className="text-[10px] text-foreground/80 leading-tight">
                  {factor}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-1 mt-2 text-center">
        <div className="bg-surface/50 p-1.5 rounded">
          <div className="text-[8px] text-muted-foreground uppercase">Delta</div>
          <div className={`text-sm font-mono font-bold ${(data.delta || 0) >= 0 ? 'text-buy' : 'text-sell'}`}>
            {((data.delta || 0) / 1000).toFixed(1)}K
          </div>
        </div>
        <div className="bg-surface/50 p-1.5 rounded">
          <div className="text-[8px] text-muted-foreground uppercase">CVD</div>
          <div className={`text-sm font-mono font-bold ${(data.cvd || 0) >= 0 ? 'text-buy' : 'text-sell'}`}>
            {((data.cvd || 0) / 1000).toFixed(1)}K
          </div>
        </div>
        <div className="bg-surface/50 p-1.5 rounded">
          <div className="text-[8px] text-muted-foreground uppercase">Speed</div>
          <div className="text-sm font-mono font-bold text-foreground">
            {data.velocity || 0}/s
          </div>
        </div>
      </div>
    </div>
  );
});

HFTSignalPanel.displayName = 'HFTSignalPanel';

// ============================================
// SIMULATION RESULTS PANEL
// ============================================
export const SimulationResultsPanel = memo(({ tradingStats, currentPrice }) => {
  const stats = tradingStats || {};
  const totalPnl = stats.total_pnl || 0;
  const totalTrades = stats.total_trades || 0;
  const winRate = stats.win_rate || 0;
  const winningTrades = stats.winning_trades || 0;
  const losingTrades = stats.losing_trades || 0;
  const openPosition = stats.open_position;
  const openEntryPrice = stats.open_entry_price;
  
  // Calculate open P&L
  let openPnl = 0;
  if (openPosition && openEntryPrice && currentPrice) {
    const points = openPosition === 'LONG' 
      ? currentPrice - openEntryPrice 
      : openEntryPrice - currentPrice;
    openPnl = points * 25; // Assuming lot size 25
  }

  return (
    <div className="p-2" data-testid="simulation-results">
      <div className="flex items-center gap-2 mb-2">
        <BarChart3 className="w-3 h-3 text-accent" />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase">Auto-Trade Simulator</span>
      </div>
      
      {/* Open Position Alert */}
      {openPosition && (
        <div className={`mb-2 p-2 rounded border ${openPosition === 'LONG' ? 'bg-buy/10 border-buy/30' : 'bg-sell/10 border-sell/30'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-xs font-bold ${openPosition === 'LONG' ? 'text-buy' : 'text-sell'}`}>
              {openPosition} @ {openEntryPrice?.toFixed(2)}
            </span>
            <span className={`text-xs font-mono font-bold ${openPnl >= 0 ? 'text-buy' : 'text-sell'}`}>
              {openPnl >= 0 ? '+' : ''}{openPnl.toFixed(0)}
            </span>
          </div>
        </div>
      )}
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2">
        {/* Total P&L */}
        <div className="bg-surface/50 p-2 rounded col-span-2">
          <div className="text-[9px] text-muted-foreground uppercase mb-1">Total P&L</div>
          <div className={`text-lg font-mono font-bold ${totalPnl >= 0 ? 'text-buy' : 'text-sell'}`}>
            ₹{totalPnl >= 0 ? '+' : ''}{totalPnl.toLocaleString()}
          </div>
        </div>
        
        {/* Win Rate */}
        <div className="bg-surface/50 p-2 rounded">
          <div className="text-[9px] text-muted-foreground uppercase mb-1">Win Rate</div>
          <div className="flex items-center gap-1">
            <Trophy className={`w-3 h-3 ${winRate >= 50 ? 'text-buy' : 'text-sell'}`} />
            <span className={`text-sm font-mono font-bold ${winRate >= 50 ? 'text-buy' : 'text-sell'}`}>
              {winRate.toFixed(0)}%
            </span>
          </div>
        </div>
        
        {/* Total Trades */}
        <div className="bg-surface/50 p-2 rounded">
          <div className="text-[9px] text-muted-foreground uppercase mb-1">Trades</div>
          <div className="text-sm font-mono font-bold text-foreground">
            {totalTrades}
          </div>
        </div>
        
        {/* Win/Loss */}
        <div className="bg-surface/50 p-2 rounded col-span-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <Trophy className="w-3 h-3 text-buy" />
              <span className="text-xs text-buy font-mono">{winningTrades}W</span>
            </div>
            <div className="flex items-center gap-1">
              <XCircle className="w-3 h-3 text-sell" />
              <span className="text-xs text-sell font-mono">{losingTrades}L</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

SimulationResultsPanel.displayName = 'SimulationResultsPanel';

// ============================================
// TAPE LOG (Debug Log)
// ============================================
export const TapeLog = memo(({ tape }) => {
  return (
    <div className="p-2" data-testid="tape-log">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-3 h-3 text-accent" />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase">Tape</span>
      </div>
      
      <div className="overflow-y-auto max-h-32 scrollbar-thin">
        <table className="w-full text-[9px] font-mono">
          <thead>
            <tr className="text-muted-foreground border-b border-border/50">
              <th className="text-left py-0.5">Time</th>
              <th className="text-right py-0.5">Price</th>
              <th className="text-right py-0.5">Δ</th>
              <th className="text-center py-0.5">Flags</th>
            </tr>
          </thead>
          <tbody>
            {tape.slice(-15).reverse().map((entry, i) => (
              <tr key={i} className={`border-b border-border/20 ${
                entry.trap ? 'bg-sell/10' : 
                entry.sweep ? 'bg-warning/10' : 
                entry.imbalance ? 'bg-accent/10' : ''
              }`}>
                <td className="py-0.5 text-muted-foreground">{entry.time}</td>
                <td className="py-0.5 text-right text-foreground">{entry.price?.toFixed(2)}</td>
                <td className={`py-0.5 text-right ${entry.delta >= 0 ? 'text-buy' : 'text-sell'}`}>
                  {entry.delta >= 0 ? '+' : ''}{entry.delta}
                </td>
                <td className="py-0.5 text-center">
                  {entry.sweep && <span className="text-warning">{entry.sweep}</span>}
                  {entry.trap && <span className="text-sell ml-1">🪤</span>}
                  {entry.imbalance && !entry.trap && (
                    <span className={entry.imbalance === 'BULLISH' ? 'text-buy' : 'text-sell'}>
                      {entry.imbalance === 'BULLISH' ? '📈' : '📉'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

TapeLog.displayName = 'TapeLog';

export default { HFTSignalPanel, TapeLog };
